<template>
  <div class="flex flex-col gap-3 self-stretch">
    <div class="sub-settings-header">
      <UITextMdMedium>{{ t("common.payment.bumProduct.bumpProducts") }}</UITextMdMedium>
    </div>
    <Switch
      :label="t('common.payment.bumProduct.enableBump')"
      class="w-100"
      :value="store.payment.bumpSettings.enableBump"
      :disabled="store.bumProductList.length === 0"
      @update:value="(value) => handleEnableBump(value)"
    />
    <div v-if="store.payment.bumpSettings.enableBump" class="flex flex-col gap-[6px]">
      <UITextSmMedium class="text-gray-700" style="--tw-text-opacity: 1">
        {{ t("common.payment.bumProduct.orderBumpStyle") }}
      </UITextSmMedium>
      <div class="flex w-[328px] justify-center self-stretch">
        <UIRadioGroup
          :value="store.payment.bumpSettings.orderBumpStyle"
          name="bumpAlignment"
          @update:value="updateBumpAlignment"
        >
          <UISpace size="small" horizontal>
            <UIRadioGroupItem
              v-for="style in ORDER_BUMP_STYLES"
              :key="style.key"
              class="flex justify-center rounded-sm px-12"
              :value="style.key"
              :disabled="false"
              :compact="true"
            >
              <template #content>
                <UITextXsMedium class="flex w-[94px] justify-center rounded-sm">
                  {{ style.name }}
                </UITextXsMedium>
              </template>
            </UIRadioGroupItem>
          </UISpace>
        </UIRadioGroup>
      </div>
    </div>
    <div
      v-if="
        store.payment.bumpSettings.enableBump &&
        store.payment.bumpSettings.orderBumpStyle === 'modern'
      "
      class="flex flex-col gap-3"
    >
      <div id="bump-template" class="flex flex-col gap-[6px]">
        <UITextSmMedium class="text-gray-700" style="--tw-text-opacity: 1">{{
          t("common.payment.bumProduct.bumpTemplate")
        }}</UITextSmMedium>
        <UISelect
          id="bumpTemplate"
          :value="store.payment.bumpSettings.bumpTemplate"
          :options="BUMP_TEMPLATES"
          :disabled="false"
          class="standard-input-ghl"
          @update:value="updateBumpTemplate"
          @change="store.addToHistory()"
        ></UISelect>
      </div>
      <div id="bump-highlight-text" class="flex flex-col gap-2">
        <UITextSmMedium>{{ t("common.payment.bumProduct.highlightText") }}</UITextSmMedium>
        <UIInput
          id="bumpHighlightText"
          v-model="store.payment.bumpSettings.highlightText"
          type="text"
          class="standard-input-ghl"
        />
      </div>
      <div id="icon-template" class="flex flex-col gap-2">
        <UITextSmMedium class="text-gray-700" style="--tw-text-opacity: 1">{{
          t("common.payment.bumProduct.icon")
        }}</UITextSmMedium>
        <UISelect
          id="bumpIcon"
          class="bump-icon-select bg-white"
          :value="store.payment.bumpSettings.bumpIcon"
          :options="BUMP_ICONS"
          :disabled="false"
          type="avatar"
          @update:value="updateBumpIcon"
          @change="store.addToHistory()"
        ></UISelect>
      </div>
      <Switch
        :label="t('common.payment.bumProduct.showPrice')"
        class="w-100"
        :value="store.payment.bumpSettings.showPrice"
        @update:value="(value) => handleShowPrice(value)"
      />
      <div
        v-if="store.payment.bumpSettings.bumpTemplate === 'templateOne'"
        class="w-100 grid grid-cols-2 gap-4"
      >
        <ColorInput
          :label="t('common.payment.bumProduct.bumpBackgroundColor')"
          :color="store.payment.bumpSettings.bumpBackgroundColor"
          typography="UITextSmMedium"
          @update:color="handleBumpBackgroundColor"
        />
        <ColorInput
          :label="t('common.payment.bumProduct.highlightTextColor')"
          typography="UITextSmMedium"
          :color="store.payment.bumpSettings.highlightTextColor"
          @update:color="handleHighlightTextColor"
        />
      </div>
      <div
        v-if="store.payment.bumpSettings.bumpTemplate === 'templateTwo'"
        class="w-100 grid grid-cols-2 gap-4"
      >
        <ColorInput
          :label="t('common.payment.bumProduct.bumpPriceColor')"
          :color="store.payment.bumpSettings.bumpPriceColor"
          typography="UITextSmMedium"
          @update:color="handleBumpPriceColor"
        />
        <ColorInput
          :label="t('common.payment.bumProduct.bumpIconColor')"
          typography="UITextSmMedium"
          :color="store.payment.bumpSettings.bumpIconColor"
          @update:color="handleBumpIconColor"
        />
      </div>
      <div
        v-if="store.payment.bumpSettings.bumpTemplate === 'templateTwo'"
        class="w-100 grid grid-cols-2 gap-4"
      >
        <ColorInput
          :label="t('common.payment.bumProduct.bumpBackgroundColor')"
          :color="store.payment.bumpSettings.bumpBackgroundColor"
          typography="UITextSmMedium"
          @update:color="handleBumpBackgroundColor"
        />
        <ColorInput
          :label="t('common.payment.bumProduct.highlightTextColor')"
          :color="store.payment.bumpSettings.highlightTextColor"
          typography="UITextSmMedium"
          @update:color="handleHighlightTextColor"
        />
      </div>

      <div
        v-if="store.payment.bumpSettings.bumpTemplate === 'templateThree'"
        class="w-100 grid grid-cols-2 gap-4"
      >
        <ColorInput
          :label="t('common.payment.bumProduct.bumpPriceColor')"
          typography="UITextSmMedium"
          :color="store.payment.bumpSettings.bumpPriceColor"
          @update:color="handleBumpPriceColor"
        />
        <ColorInput
          :label="t('common.payment.bumProduct.bumpBorderColor')"
          :color="store.payment.bumpSettings.bumpBorderColor"
          typography="UITextSmMedium"
          @update:color="handleBumpBorderColor"
        />
        <ColorInput
          :label="t('common.payment.bumProduct.highlightTextColor')"
          :color="store.payment.bumpSettings.highlightTemplateThreeColor"
          typography="UITextSmMedium"
          @update:color="handleHighlightTemplateThreeColor"
        />
        <ColorInput
          :label="t('common.payment.bumProduct.headlineColor')"
          :color="store.payment.bumpSettings.headlineColor"
          typography="UITextSmMedium"
          @update:color="handleHeadlineColor"
        />
        <ColorInput
          :label="t('common.payment.bumProduct.otoHeadlineColor')"
          typography="UITextSmMedium"
          :color="store.payment.bumpSettings.otoHeadlineColor"
          @update:color="handleOtoHeadlineColor"
        />
        <ColorInput
          :label="t('common.payment.bumProduct.otoTextColor')"
          typography="UITextSmMedium"
          :color="store.payment.bumpSettings.otoTextColor"
          @update:color="handleOtoTextColor"
        />
      </div>
      <div
        v-if="store.payment.bumpSettings.bumpTemplate !== 'templateThree'"
        class="w-100 grid grid-cols-2 gap-4"
      >
        <ColorInput
          :label="t('common.payment.bumProduct.headlineColor')"
          :color="store.payment.bumpSettings.headlineColor"
          typography="UITextSmMedium"
          @update:color="handleHeadlineColor"
        />
        <ColorInput
          :label="t('common.payment.bumProduct.otoHeadlineColor')"
          typography="UITextSmMedium"
          :color="store.payment.bumpSettings.otoHeadlineColor"
          @update:color="handleOtoHeadlineColor"
        />
      </div>
      <ColorInput
        v-if="store.payment.bumpSettings.bumpTemplate !== 'templateThree'"
        :label="t('common.payment.bumProduct.otoTextColor')"
        typography="UITextSmMedium"
        :color="store.payment.bumpSettings.otoTextColor"
        @update:color="handleOtoTextColor"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import Switch from "@/components/common/Elements/Switch.vue";
import { useAppStore } from "@/store/app";
import { BUMP_ICONS, BUMP_TEMPLATES, ORDER_BUMP_STYLES } from "@/util/productConstants";
import {
  UIInput,
  UIRadioGroup,
  UIRadioGroupItem,
  UISelect,
  UISpace,
  UITextMdMedium,
  UITextSmMedium,
} from "@gohighlevel/ghl-ui";
import { h } from "vue";
import { useI18n } from "vue-i18n";
import ColorInput from "../../Elements/ColorInput.vue";

const { t } = useI18n();
const store = useAppStore();

const renderLabel = (option, selected) => {
  return h("div", {
    class: "flex items-center gap-2",
  });
};
const updateBumpAlignment = (alignment: string) => {
  store.payment.bumpSettings.orderBumpStyle = alignment;
  store.anyUnsavedChanges = true;
};

const handleEnableBump = (value: boolean) => {
  store.payment.bumpSettings.enableBump = value;
  store.anyUnsavedChanges = true;
};

const updateBumpTemplate = (template: string) => {
  store.payment.bumpSettings.bumpTemplate = template;
  store.anyUnsavedChanges = true;
};

const handleShowPrice = (value: boolean) => {
  store.payment.bumpSettings.showPrice = value;
  store.anyUnsavedChanges = true;
};
const handleBumpPriceColor = (color: string) => {
  store.payment.bumpSettings.bumpPriceColor = color;
  store.anyUnsavedChanges = true;
};
const handleBumpIconColor = (color: string) => {
  store.payment.bumpSettings.bumpIconColor = color;
  store.anyUnsavedChanges = true;
};

const handleBumpBackgroundColor = (color: string) => {
  store.payment.bumpSettings.bumpBackgroundColor = color;
  store.anyUnsavedChanges = true;
};
const handleHighlightTextColor = (color: string) => {
  store.payment.bumpSettings.highlightTextColor = color;
  store.anyUnsavedChanges = true;
};

const updateBumpIcon = (icon: string) => {
  store.payment.bumpSettings.bumpIcon = icon;
  store.anyUnsavedChanges = true;
};

const handleHeadlineColor = (color: string) => {
  store.payment.bumpSettings.headlineColor = color;
  store.anyUnsavedChanges = true;
};

const handleOtoHeadlineColor = (color: string) => {
  store.payment.bumpSettings.otoHeadlineColor = color;
  store.anyUnsavedChanges = true;
};
const handleOtoTextColor = (color: string) => {
  store.payment.bumpSettings.otoTextColor = color;
  store.anyUnsavedChanges = true;
};

const handleBumpBorderColor = (color: string) => {
  store.payment.bumpSettings.bumpBorderColor = color;
  store.anyUnsavedChanges = true;
};

const handleHighlightTemplateThreeColor = (color: string) => {
  store.payment.bumpSettings.highlightTemplateThreeColor = color;
  store.anyUnsavedChanges = true;
};
</script>

<style scoped>
#bumpIcon :deep(.n-avatar) {
  background: transparent !important;
}

#bumpIcon :deep(.n-base-selection-input__content) div {
  display: flex;
  align-items: start;
  justify-content: start;
  gap: 8px;
}
#bumpIcon :deep(.n-base-select-option__content) div {
  background: transparent !important;
}
</style>
